import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RestartAltOutlined, SaveOutlined } from "@mui/icons-material";
import { Switch } from "@mui/material";
import dayjs from "../../../../Utils/constants/dayjsConfig";
import { getVenueVisibility, updateVenueVisibility } from "../../../../services/Dashboard/Table";
import { DATE_FORMAT, AUSTRALIAN_TIMEZONE } from "../../../../Utils/constants/constants";
import { formatDateOnly } from "../../../../Utils/formatDateTime";

const VENUE_VISIBILITY_TIMEZONE = AUSTRALIAN_TIMEZONE;

function VenueStatusPill({ enabled }) {
  return (
    <span className={`inline-flex items-center justify-center min-h-7 px-2.5 py-2 rounded-full border md:text-xs text-[10px] font-extrabold uppercase tracking-[0.04em] ${enabled ? "border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)]" : "border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] text-[var(--color-accent-red)]"}`}>
      {enabled ? "Shown" : "Hidden"}
    </span>
  );
}

function VenuesModule() {
  const dateOptions = useMemo(() => {
    const melbourneToday = dayjs().tz(VENUE_VISIBILITY_TIMEZONE);
    return [
      { id: "today", label: "Today", date: melbourneToday.format(DATE_FORMAT) },
      { id: "tomorrow", label: "Tomorrow", date: melbourneToday.add(1, "day").format(DATE_FORMAT) },
      { id: "day-after", label: "Day After", date: melbourneToday.add(2, "day").format(DATE_FORMAT) },
    ];
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  const [activeDate, setActiveDate] = useState(() => {
    const saved = location.state?.adminVenuesActiveDate;
    if (saved && dateOptions.some((opt) => opt.date === saved)) {
      return saved;
    }
    return dateOptions[0].date;
  });

  useEffect(() => {
    if (location.state?.adminVenuesActiveDate !== activeDate) {
      navigate(location.pathname, {
        state: { ...location.state, adminVenuesActiveDate: activeDate },
        replace: true,
      });
    }
  }, [activeDate, location.state, navigate, location.pathname]);
  const [venuesByDate, setVenuesByDate] = useState({});
  const [venueVisibility, setVenueVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState("");

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    let ignore = false;
    const fetchVenueDates = async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const venueResponses = await Promise.all(
          dateOptions.map(async ({ date }) => {
            const data = await getVenueVisibility(date);
            return [date, (data.results || []).map((venue) => ({ id: venue.venue_id, name: venue.venue_name, isEnabled: venue.is_enabled }))];
          })
        );
        if (ignore) return;
        const nextVenuesByDate = Object.fromEntries(venueResponses);
        setVenuesByDate(nextVenuesByDate);
        setVenueVisibility((current) => {
          const nextVisibility = { ...current };
          dateOptions.forEach(({ date }) => {
            const currentDateVisibility = nextVisibility[date] || {};
            nextVisibility[date] = {};
            (nextVenuesByDate[date] || []).forEach(({ id }) => {
              nextVisibility[date][id] = currentDateVisibility[id] ?? nextVenuesByDate[date].find((venue) => venue.id === id)?.isEnabled ?? true;
            });
          });
          return nextVisibility;
        });
      } catch (error) {
        if (!ignore) setErrorMessage(error?.error || "Unable to load venues for these dates.");
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchVenueDates();
    return () => { ignore = true; };
  }, [dateOptions]);

  const activeVenues = venuesByDate[activeDate] || [];
  const activeVisibility = venueVisibility[activeDate] || {};
  const enabledCount = activeVenues.filter(({ id }) => activeVisibility[id] !== false).length;
  const disabledCount = activeVenues.length - enabledCount;

  const setVenueEnabled = (date, venueId, enabled) => {
    setVenueVisibility((current) => ({ ...current, [date]: { ...(current[date] || {}), [venueId]: enabled } }));
    setLastSavedAt("");
  };

  const setAllVenuesForDate = (date, enabled) => {
    const venues = venuesByDate[date] || [];
    if (venues.length === 0) return;
    setVenueVisibility((current) => {
      const nextDateVisibility = {};
      venues.forEach(({ id }) => { nextDateVisibility[id] = enabled; });
      return { ...current, [date]: nextDateVisibility };
    });
    setLastSavedAt("");
  };

  const handleSaveSelection = () => {
    if (activeVenues.length === 0 || loading || saving) return;
    setSaving(true);
    setErrorMessage("");
    const venues = activeVenues.map(({ id }) => ({ venue_id: id, is_enabled: activeVisibility[id] !== false }));
    updateVenueVisibility(activeDate, venues)
      .then((data) => {
        const savedVenues = (data.results || []).map((venue) => ({ id: venue.venue_id, name: venue.venue_name, isEnabled: venue.is_enabled }));
        setVenuesByDate((current) => ({ ...current, [activeDate]: savedVenues }));
        setVenueVisibility((current) => ({ ...current, [activeDate]: Object.fromEntries(savedVenues.map((venue) => [venue.id, venue.isEnabled])) }));
        setLastSavedAt(dayjs().tz(VENUE_VISIBILITY_TIMEZONE).format("hh:mm A"));
      })
      .catch((error) => { setErrorMessage(error?.error || "Unable to save venue visibility."); })
      .finally(() => { setSaving(false); });
  };

  const switchSx = { "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--color-primary)" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "var(--color-primary)" } };

  return (
    <div className="grid gap-4">
      <div className="admin-venue-date-grid grid grid-cols-3 gap-4">
        {dateOptions.map(({ id, label, date }) => {
          const isActive = date === activeDate;
          const venuesForDate = venuesByDate[date] || [];
          const visibleForDate = venuesForDate.filter(({ id }) => venueVisibility[date]?.[id] !== false).length;
          return (
            <button key={id} type="button" onClick={() => setActiveDate(date)}
              className={`flex flex-col gap-1 min-h-[88px] p-4 border rounded-[var(--radius-md)] text-[var(--color-text)] cursor-pointer text-left ${isActive ? "border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)]" : "border-[var(--color-border)] bg-[var(--color-surface)]"}`}>
              <span className={`md:text-sm text-xs label-caps block m-0 ${isActive ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"}`}>{label}</span>
              <span className="tabular block xl:text-xl md:text-lg text-base font-extrabold text-[var(--color-text)]">{formatDateOnly(date)}</span>
              <span className="block xl:text-sm md:text-xs text-[10px] text-[var(--color-text-muted)]">{visibleForDate}/{venuesForDate.length} venues shown</span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)]">
        <div className="flex flex-col gap-1">
          <p className="md:text-sm text-xs label-caps block m-0">Venue Date</p>
          <h4 className="xl:text-xl md:text-lg text-base text-[var(--color-text)]">{dayjs(activeDate).tz(VENUE_VISIBILITY_TIMEZONE).format("dddd, DD MMMM YYYY")}</h4>
          <p className="xl:text-sm md:text-xs text-[10px] text-[var(--color-text-muted)]">{enabledCount} enabled, {disabledCount} disabled</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setAllVenuesForDate(activeDate, true)}
            disabled={loading || activeVenues.length === 0}
            className={`min-h-[38px] px-3 py-2.5 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-transparent text-[var(--color-text)] md:text-sm text-xs font-bold transition-opacity ${loading || activeVenues.length === 0 ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
          >
            Show all
          </button>
          <button
            type="button"
            onClick={() => setAllVenuesForDate(activeDate, false)}
            disabled={loading || activeVenues.length === 0}
            className={`min-h-[38px] px-3 py-2.5 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-transparent text-[var(--color-text)] md:text-sm text-xs font-bold transition-opacity ${loading || activeVenues.length === 0 ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
          >
            Hide all
          </button>
          <button
            type="button"
            onClick={() => {
              if (activeVenues.length === 0) return;
              const reset = {};
              (venuesByDate[activeDate] || []).forEach((v) => { reset[v.id] = v.isEnabled; });
              setVenueVisibility((current) => ({ ...current, [activeDate]: reset }));
            }}
            disabled={loading || activeVenues.length === 0}
            className={`inline-flex items-center gap-1.5 min-h-[38px] px-3 py-2.5 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-transparent text-[var(--color-text-muted)] md:text-sm text-xs font-bold transition-opacity ${activeVenues.length === 0 ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
          >
            <RestartAltOutlined sx={{ fontSize: 17 }} />Reset
          </button>
        </div>
      </div>

      {errorMessage && <div className="px-4 py-3.5 border border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] rounded-[var(--radius-md)] text-[var(--color-accent-red)] md:text-sm text-xs">{errorMessage}</div>}

      <div className="border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)] overflow-hidden">
        {loading ? (
          <div className="grid gap-px">{[1, 2, 3].map((item) => <div key={item} className="skeleton h-[70px] bg-[rgba(255,255,255,0.04)]" />)}</div>
        ) : activeVenues.length === 0 ? (
          <div className="min-h-[180px] grid place-items-center xl:text-xl md:text-lg text-base text-[var(--color-text-muted)] text-center">No venues found for {formatDateOnly(activeDate)}.</div>
        ) : (
          activeVenues.map(({ id, name }) => {
            const enabled = activeVisibility[id] !== false;
            return (
              <div key={`${activeDate}-${id}`} className="admin-venue-row grid grid-cols-[minmax(0,1fr)_auto_auto] gap-4 items-center min-h-[74px] p-4 first:border-t-0 border-t border-[var(--color-border)]">
                <div className="flex flex-col gap-1 min-w-0">
                  <h5 className={`m-0 xl:text-lg md:text-base text-sm font-extrabold truncate ${enabled ? "text-[var(--color-text)]" : "text-[var(--color-text-muted)]"}`}>{name}</h5>
                  <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)]">{formatDateOnly(activeDate)}</p>
                </div>
                <VenueStatusPill enabled={enabled} />
                <Switch checked={enabled} onChange={(event) => setVenueEnabled(activeDate, id, event.target.checked)} inputProps={{ "aria-label": `${enabled ? "Hide" : "Show"} ${name}` }} sx={switchSx} />
              </div>
            );
          })
        )}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="m-0 text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs leading-[1.5]">
          These controls are limited to today, tomorrow, and the day after in Melbourne time.
          {lastSavedAt && <span className="tabular"> Saved at {lastSavedAt}.</span>}
        </p>
        <button
          type="button"
          onClick={handleSaveSelection}
          disabled={loading || saving || activeVenues.length === 0}
          className={`inline-flex items-center justify-center gap-2 min-h-[42px] px-3 py-2.5 border rounded-[var(--radius-md)] text-black md:text-sm text-xs font-black transition-all ${
            loading || saving || activeVenues.length === 0
              ? "cursor-not-allowed opacity-40 bg-[rgba(200,255,0,0.5)] border-transparent"
              : "cursor-pointer bg-[var(--color-primary)] border-[var(--color-primary)]"
          }`}
        >
          <SaveOutlined sx={{ fontSize: 18 }} />
          {saving ? "Saving..." : "Save selection"}
        </button>
      </div>
    </div>
  );
}

export default VenuesModule;
